import { ChevronDown } from 'lucide-react'
import { useId } from 'react'

import { cn } from '@/lib/cn'

export interface SelectOption<T extends string> {
  value: T
  label: string
}

export interface SelectFieldProps<T extends string> {
  label: string
  value: T
  options: readonly SelectOption<T>[]
  onChange: (value: T) => void
  hint?: string
  /** Inline validation message. Replaces the hint while present. */
  error?: string
  disabled?: boolean
  className?: string
}

/**
 * A native `<select>`. It gets the platform picker on mobile for free, which is
 * a better control than any listbox we would build.
 */
export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
  error,
  disabled,
  className,
}: SelectFieldProps<T>) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
      </label>

      <div className="relative flex items-center">
        <select
          id={id}
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          onChange={(event) => {
            // Narrowing through the option list keeps the union honest without a cast.
            const next = options.find((option) => option.value === event.target.value)
            if (next) onChange(next.value)
          }}
          className={cn(
            'h-11 w-full appearance-none rounded-lg border border-border bg-surface-raised pl-3 pr-10 text-base text-text',
            'transition-colors hover:border-border-strong',
            'disabled:cursor-not-allowed disabled:opacity-60',
            // Not a colour override: `color-scheme` is what tells the browser to
            // paint the native dropdown list itself dark. Tokens cannot reach it.
            '[color-scheme:light] dark:[color-scheme:dark]',
            error && 'border-danger',
          )}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDown
          className="pointer-events-none absolute right-3 h-4 w-4 text-text-subtle"
          aria-hidden="true"
        />
      </div>

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
